import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/cn';
import {
  buildGifSearchUrl,
  parseGifSearchResponse,
  type GifHit,
} from '../../lib/gifSearch';

const CATEGORIES = [
  { label: 'Trending', query: '' },
  { label: 'LOL', query: 'lol' },
  { label: 'OMG', query: 'omg' },
  { label: 'Bravo', query: 'applause' },
  { label: '👍', query: 'thumbs up' },
  { label: 'Dance', query: 'dance' },
  { label: 'Sad', query: 'sad' },
  { label: 'Angry', query: 'angry' },
  { label: 'Love', query: 'love' },
  { label: '🤯', query: 'mind blown' },
];

interface GifPickerProps {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onSelect: (gifUrl: string) => void;
  onClose: () => void;
}

export function GifPicker({ anchorRef, onSelect, onClose }: GifPickerProps) {
  const [search, setSearch] = useState('');
  const [gifs, setGifs] = useState<GifHit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Compute position above the anchor button (ownerDocument so PiP works).
  const ownerDoc = anchorRef.current?.ownerDocument ?? document;
  const view = ownerDoc.defaultView ?? window;
  const rect = anchorRef.current?.getBoundingClientRect();
  const popupWidth = 384; // w-96
  const left = rect ? Math.max(8, Math.min(rect.left, view.innerWidth - popupWidth - 8)) : 8;
  const bottom = rect ? view.innerHeight - rect.top + 8 : 200;

  const fetchGifs = useCallback(async (query: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(buildGifSearchUrl(query));
      if (!res.ok) throw new Error();
      const data: unknown = await res.json();
      setGifs(parseGifSearchResponse(data));
    } catch {
      setError('Impossible de charger les GIFs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGifs('');
  }, [fetchGifs]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchGifs(search), 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [search, fetchGifs]);

  return createPortal(
    <div
      data-cime-modal
      className="fixed z-[70] w-96 rounded-xl border border-cime-border bg-cime-surface shadow-2xl"
      style={{ left, bottom }}
    >
      {/* Search */}
      <div className="flex items-center gap-2 border-b border-cime-border p-3">
        <Search className="w-4 h-4 text-cime-text-muted flex-shrink-0" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un GIF…"
          className="flex-1 bg-transparent text-sm text-cime-text-primary placeholder:text-cime-text-muted outline-none"
          autoFocus
        />
        <button type="button" onClick={onClose} className="text-cime-text-muted hover:text-cime-text-primary transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto p-2 scrollbar-none">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.label}
            type="button"
            onClick={() => setSearch(cat.query)}
            className={cn(
              'whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors flex-shrink-0',
              (cat.query === '' && search === '') || search === cat.query
                ? 'bg-cime-primary text-white'
                : 'bg-cime-bg text-cime-text-muted border border-cime-border hover:border-cime-primary hover:text-cime-primary',
            )}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* GIF grid */}
      <div className="h-64 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-cime-primary" />
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-cime-text-muted">
            <AlertCircle className="w-6 h-6 text-red-400" />
            {error}
          </div>
        ) : gifs.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-cime-text-muted">
            Aucun GIF trouvé
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {gifs.map((gif) => (
              <button
                key={gif.id}
                type="button"
                onClick={() => onSelect(gif.url)}
                className="group relative aspect-square overflow-hidden rounded-lg bg-cime-bg hover:scale-105 transition-transform"
              >
                <img
                  src={gif.previewUrl}
                  alt={gif.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Attribution */}
      <div className="border-t border-cime-border flex justify-center p-2">
        <span className="text-xs text-cime-text-muted">Powered by GIPHY</span>
      </div>
    </div>,
    ownerDoc.body,
  );
}
